import {
  Fragment,
  forwardRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from "react";

import {
  friendlyTime12h,
  layoutCourtGridItems,
  rowPlacement,
  timeFromMinutes,
  type CourtGridInterval,
  type CourtGridLayout,
  type GridRange,
  type LaneInfo,
} from "../lib/scheduleGrid";
import { courtDisplayName, type CourtHalf } from "../lib/courts";
import { compactDateEs, formatLongDateEs } from "../lib/dateFormat";
import Icon from "./icons";

/** Estado de una celda de 15 minutos (= un posible INICIO):
 * free = se puede tocar; idle = cabría pero falta elegir partido; nofit = no
 * cabe ahí (choque o bloqueo); outside = fuera de Desde–Último inicio. */
export type CourtTimeGridTone = "free" | "idle" | "nofit" | "outside";

export interface CourtTimeGridCell {
  enabled: boolean;
  tone: CourtTimeGridTone;
  title: string;
}

export interface CourtTimeGridItemInfo extends LaneInfo {
  /** Filas que abarca la tarjeta (1 fila = 15 min). */
  span: number;
  /** Mini cancha de la tarjeta ("A".."D"; null = cancha completa). */
  half: CourtHalf | null;
  /** Tarjeta angosta (media columna o carril): la página la dibuja compacta. */
  narrow: boolean;
}

export interface CourtTimeGridCourt {
  key: string;
  label: ReactNode;
  /** Nombre completo en texto (título y lector de pantalla) cuando `label`
   * no es texto plano (p. ej. "Cancha Central · Pádel"). */
  text?: string;
  /** Mini canchas (Eduardo 2026-10-01): cancha física ("9") y mini cancha
   * ("A".."D"). Las columnas con `sub` van juntas bajo un encabezado
   * «Cancha 9» y abajo «A» / «B» (/ «C» / «D»); sin `sub` en ninguna columna el grid es
   * el de siempre. */
  group?: string;
  sub?: CourtHalf;
}

/** Inicio propuesto que aún no se guarda: se dibuja como contorno. */
export interface CourtTimeGridPreview {
  court: string;
  startMin: number;
  endMin: number;
}

interface CourtTimeGridProps<I extends CourtGridInterval> {
  courts: CourtTimeGridCourt[];
  range: GridRange;
  items: I[];
  /** Bloque (min) de los partidos. Escala el alto de fila para que un
   * bloque mida lo mismo que la fila de antes: con bloques de 90 el
   * calendario NO crece de alto (Eduardo 2026-10-01: "sí se hace más alto"). */
  blockMinutes?: number;
  cellState: (court: string, slotMin: number) => CourtTimeGridCell;
  /** Toque en una celda que admite inicio. `viaKeyboard`: se activó con
   * Enter/Espacio (la página puede mover el foco a Confirmar). */
  onCellClick: (court: string, time: string, info: { viaKeyboard: boolean }) => void;
  /** Toque en una celda que no admite inicio: la página explica por qué
   * (en móvil no hay tooltip). */
  onDisabledCellClick?: (court: string, time: string, cell: CourtTimeGridCell) => void;
  renderItem: (item: I, info: CourtTimeGridItemInfo) => ReactNode;
  /** Tarjeta que deja pasar los toques a las celdas de abajo (el partido que
   * se está moviendo: así puede recorrerse 15 min dentro de su propio hueco;
   * el toque solo PROPONE la hora, nada se guarda sin Confirmar). */
  passThroughKey?: string | null;
  /** Hora propuesta (sin guardar) para el partido elegido. */
  preview?: CourtTimeGridPreview | null;
  busy?: boolean;
  /** Al cambiar `scrollKey` el visor se desplaza a la fila de `scrollToMin`. */
  scrollKey?: string;
  scrollToMin?: number | null;
  /** Columna a la que se recorre el visor de lado (al cambiar `scrollKey` o
   * esta llave): en el celular una categoría de Mini cancha abre en sus
   * mini canchas (9-A) y no en las canchas 1 y 2. Si pasa de una columna a
   * null (otra categoría), el visor regresa a la primera cancha. */
  scrollToCourtKey?: string | null;
  /** Hay un partido elegido: las celdas libres se resaltan más. */
  placing?: boolean;
  ariaLabel?: string;
  /** Fecha que muestra el grid (YYYY-MM-DD): va en la esquina fija de
   * arriba a la izquierda, así se ve aunque el grid esté desplazado
   * (Eduardo 2026-10-01: «me pierdo con la fecha»). Sin fecha, "Hora". */
  date?: string | null;
}

type Placed<I> = {
  item: I;
  row: number;
  span: number;
  offsetTop: number;
  offsetBottom: number;
  layout: CourtGridLayout;
};

/** `headRows`: filas de encabezado (2 con mini canchas: «Cancha 9» y abajo
 * «A» / «B» …). `columnSpan` 2..4 = cancha completa sobre sus mini canchas. */
function itemStyle(
  row: number,
  span: number,
  column: number,
  offsetTop: number,
  offsetBottom: number,
  headRows: number,
  columnSpan: number = 1,
): CSSProperties {
  return {
    gridRow: `${row + headRows + 1} / span ${span}`,
    gridColumn: columnSpan > 1 ? `${column + 2} / span ${columnSpan}` : column + 2,
    ["--off-top" as string]: offsetTop ? offsetTop.toFixed(3) : "0",
    ["--off-bottom" as string]: offsetBottom ? offsetBottom.toFixed(3) : "0",
  };
}

/** Grid de canchas × filas de 15 minutos con UNA tarjeta por partido o
 * bloqueo que abarca las filas de su duración (Eduardo 2026-10-01: "filas de
 * 15 min, tarjeta de corrido"). El visor tiene alto máximo y su propio
 * scroll; el encabezado de canchas y la columna de horas se quedan fijos.
 *
 * Teclado: una sola celda está en el orden de Tab y las flechas recorren el
 * grid (arriba/abajo 15 min, izquierda/derecha de cancha). En el documento
 * cada tarjeta va justo después de la fila donde empieza, así el lector de
 * pantalla las lee en el orden en que se ven. */
export default function CourtTimeGrid<I extends CourtGridInterval>({
  courts,
  range,
  items,
  blockMinutes,
  cellState,
  onCellClick,
  onDisabledCellClick,
  renderItem,
  passThroughKey = null,
  preview = null,
  busy = false,
  scrollKey,
  scrollToMin = null,
  scrollToCourtKey = null,
  placing = false,
  ariaLabel = "Calendario de canchas",
  date = null,
}: CourtTimeGridProps<I>) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const [focusPos, setFocusPos] = useState<{ row: number; col: number } | null>(null);
  const courtIndex = useMemo(() => new Map(courts.map((court, index) => [court.key, index])), [courts]);
  const courtText = useMemo(
    () =>
      new Map(
        courts.map((court) => [
          court.key,
          court.text ?? (typeof court.label === "string" ? court.label : court.key),
        ]),
      ),
    [courts],
  );

  // Mini canchas: dos filas de encabezado solo si alguna columna es mitad.
  const hasSub = courts.some((court) => Boolean(court.sub));
  const headRows = hasSub ? 2 : 1;
  const columnKeys = useMemo(() => courts.map((court) => court.key), [courts]);

  // Cada tarjeta en su columna (o las dos de una cancha dividida, o media
  // columna si es una mini cancha en una columna sin dividir); lo que no
  // cabe en ninguna columna no se dibuja (la página lo cuenta y lo avisa).
  const placedByRow = useMemo(() => {
    const layouts = layoutCourtGridItems(items, columnKeys);
    const byRow = new Map<number, Array<Placed<I>>>();
    for (const item of items) {
      const layout = layouts.get(item.key);
      if (!layout) continue;
      const placement = rowPlacement(item.startMin, item.endMin, range);
      if (!placement) continue;
      const list = byRow.get(placement.row) ?? [];
      list.push({
        item,
        row: placement.row,
        span: placement.span,
        offsetTop: placement.offsetTop,
        offsetBottom: placement.offsetBottom,
        layout,
      });
      byRow.set(placement.row, list);
    }
    for (const list of byRow.values()) {
      list.sort((a, b) => a.layout.column - b.layout.column || a.layout.lane - b.layout.lane);
    }
    return byRow;
  }, [items, columnKeys, range]);

  const previewPlacement = useMemo(() => {
    if (!preview || !courtIndex.has(preview.court)) return null;
    const placement = rowPlacement(preview.startMin, preview.endMin, range);
    if (!placement) return null;
    return { ...placement, column: courtIndex.get(preview.court) ?? 0 };
  }, [preview, courtIndex, range]);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport || scrollToMin === null || scrollToMin === undefined) return;
    const target = Math.max(range.startMin, Math.floor(scrollToMin / range.rowMinutes) * range.rowMinutes);
    const label = viewport.querySelector<HTMLElement>(`[data-row-min="${target}"]`);
    const header = viewport.querySelector<HTMLElement>(".schedule-timegrid__corner");
    if (!label) return;
    viewport.scrollTop = Math.max(0, label.offsetTop - (header?.offsetHeight ?? 0));
    // Solo al cambiar de fecha/sede/horario: no "jalar" el scroll en cada recarga.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollKey]);

  // Columna destino anterior: al pasar de una (categoría Mini, 9-A) a
  // ninguna (otra categoría, mismo día y sede) el grid sigue montado y se
  // quedaría corrido a la derecha; se regresa al inicio.
  const previousScrollToCourtKey = useRef<string | null>(scrollToCourtKey);

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    const previous = previousScrollToCourtKey.current;
    previousScrollToCourtKey.current = scrollToCourtKey;
    if (!viewport) return;
    if (!scrollToCourtKey) {
      if (previous) viewport.scrollLeft = 0;
      return;
    }
    const head = Array.from(viewport.querySelectorAll<HTMLElement>("[data-court-head]")).find(
      (element) => element.dataset.courtHead === scrollToCourtKey,
    );
    if (!head) return;
    const corner = viewport.querySelector<HTMLElement>(".schedule-timegrid__corner");
    viewport.scrollLeft = Math.max(0, head.offsetLeft - (corner?.offsetWidth ?? 0));
    // Solo al cambiar de fecha/sede/horario o de columna destino.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollKey, scrollToCourtKey]);

  // Estados de todas las celdas (una pasada por render, como antes).
  const cells = range.rows.map((slotMin) => courts.map((court) => cellState(court.key, slotMin)));

  // Celda en el orden de Tab: la última enfocada o, si no hay, la primera
  // libre (o la primera del grid).
  let tabRow = 0;
  let tabCol = 0;
  if (focusPos && focusPos.row < range.rows.length && focusPos.col < courts.length) {
    tabRow = focusPos.row;
    tabCol = focusPos.col;
  } else {
    outer: for (let r = 0; r < cells.length; r += 1) {
      for (let c = 0; c < cells[r].length; c += 1) {
        if (cells[r][c].enabled) {
          tabRow = r;
          tabCol = c;
          break outer;
        }
      }
    }
  }

  function focusCell(row: number, col: number) {
    const nextRow = Math.max(0, Math.min(range.rows.length - 1, row));
    const nextCol = Math.max(0, Math.min(courts.length - 1, col));
    setFocusPos({ row: nextRow, col: nextCol });
    viewportRef.current
      ?.querySelector<HTMLElement>(`.schedule-timegrid__cell[data-row="${nextRow}"][data-col="${nextCol}"]`)
      ?.focus();
  }

  function handleCellKeyDown(event: ReactKeyboardEvent<HTMLButtonElement>, row: number, col: number) {
    const moves: Record<string, [number, number]> = {
      ArrowUp: [row - 1, col],
      ArrowDown: [row + 1, col],
      ArrowLeft: [row, col - 1],
      ArrowRight: [row, col + 1],
      Home: [row, 0],
      End: [row, courts.length - 1],
      PageUp: [row - 4, col],
      PageDown: [row + 4, col],
    };
    const next = moves[event.key];
    if (!next) return;
    event.preventDefault();
    focusCell(next[0], next[1]);
  }

  // Una mini cancha mide media columna (mínimo --tg-half-min): una cancha
  // dividida ocupa lo mismo que una completa.
  const halfCount = courts.filter((court) => court.sub).length;
  const gridStyle: CSSProperties = hasSub
    ? {
        gridTemplateColumns: `var(--tg-time-w) ${courts
          .map((court) => (court.sub ? "minmax(var(--tg-half-min), 0.5fr)" : "minmax(var(--tg-col-min), 1fr)"))
          .join(" ")}`,
        gridTemplateRows: `var(--tg-head-h) var(--tg-sub-h) repeat(${range.rows.length}, var(--tg-row-h))`,
        ["--tg-courts" as string]: String(courts.length - halfCount),
        ["--tg-halves" as string]: String(halfCount),
      }
    : {
        gridTemplateColumns: `var(--tg-time-w) repeat(${courts.length}, minmax(var(--tg-col-min), 1fr))`,
        gridTemplateRows: `var(--tg-head-h) repeat(${range.rows.length}, var(--tg-row-h))`,
        ["--tg-courts" as string]: String(courts.length),
      };
  const block = blockMinutes && Number.isFinite(blockMinutes) && blockMinutes > 0 ? blockMinutes : 60;
  const viewportStyle: CSSProperties = { ["--tg-block" as string]: String(block) };
  const cornerDate = compactDateEs(date);
  const longDate = formatLongDateEs(date);

  return (
    <div className="schedule-timegrid__viewport" ref={viewportRef} style={viewportStyle}>
      <div
        className={`schedule-timegrid${placing ? " is-placing" : ""}`}
        style={gridStyle}
        role="group"
        aria-label={`${ariaLabel}${longDate ? ` del ${longDate.toLowerCase()}` : ""}. Usa las flechas para recorrer las horas y canchas.`}
      >
        {cornerDate ? (
          <div
            className="schedule-timegrid__corner schedule-timegrid__corner--date"
            style={{ gridRow: hasSub ? "1 / span 2" : 1, gridColumn: 1 }}
            title={longDate}
            data-grid-date={date ?? undefined}
          >
            <span>{cornerDate.weekday}</span>
            <span>{cornerDate.day}</span>
          </div>
        ) : (
          <div className="schedule-timegrid__corner" style={{ gridRow: hasSub ? "1 / span 2" : 1, gridColumn: 1 }}>
            Hora
          </div>
        )}
        {courts.map((court, index) => {
          if (!court.sub) {
            return (
              <div
                key={`head-${court.key}`}
                className="schedule-timegrid__court-head"
                style={{ gridRow: hasSub ? "1 / span 2" : 1, gridColumn: index + 2 }}
                title={courtText.get(court.key)}
                data-court-head={court.key}
              >
                <span className="schedule-timegrid__court-name">{court.label}</span>
              </div>
            );
          }
          // Mini canchas: «Cancha 9» arriba de sus mini canchas y «A» / «B» (…) abajo.
          const previous = courts[index - 1];
          const startsGroup = !previous?.sub || previous.group !== court.group;
          let groupSize = 1;
          while (courts[index + groupSize]?.sub && courts[index + groupSize].group === court.group) groupSize += 1;
          const groupName = courtDisplayName(court.group ?? court.key);
          return (
            <Fragment key={`head-${court.key}`}>
              {startsGroup ? (
                <div
                  className="schedule-timegrid__court-head schedule-timegrid__court-head--group"
                  style={{ gridRow: 1, gridColumn: groupSize > 1 ? `${index + 2} / span ${groupSize}` : index + 2 }}
                  title={`${groupName}: dividida en ${groupSize} mini canchas`}
                  data-court-group={court.group}
                >
                  <span className="schedule-timegrid__court-name">{groupName}</span>
                </div>
              ) : null}
              <div
                className="schedule-timegrid__court-head schedule-timegrid__court-head--sub"
                style={{ gridRow: 2, gridColumn: index + 2 }}
                title={courtText.get(court.key)}
                data-court-head={court.key}
              >
                <span className="schedule-timegrid__court-name">{court.sub}</span>
              </div>
            </Fragment>
          );
        })}
        {range.rows.map((slotMin, rowIndex) => {
          const time = timeFromMinutes(slotMin);
          const quarter = slotMin % 60 === 0 ? " is-hour" : slotMin % 30 === 0 ? " is-half" : " is-quarter";
          const rowItems = placedByRow.get(rowIndex) ?? [];
          return (
            <Fragment key={slotMin}>
              <div
                className={`schedule-timegrid__time${quarter}`}
                style={{ gridRow: rowIndex + headRows + 1, gridColumn: 1 }}
                data-row-min={slotMin}
                aria-hidden="true"
              >
                {time}
              </div>
              {courts.map((court, courtIdx) => {
                const cell = cells[rowIndex][courtIdx];
                return (
                  <button
                    type="button"
                    key={`${slotMin}-${court.key}`}
                    className={`schedule-timegrid__cell is-${cell.tone}${quarter}${court.sub ? ` is-half-column is-half-${court.sub.toLowerCase()}` : ""}`}
                    style={{ gridRow: rowIndex + headRows + 1, gridColumn: courtIdx + 2 }}
                    disabled={busy}
                    aria-disabled={cell.enabled ? undefined : true}
                    tabIndex={rowIndex === tabRow && courtIdx === tabCol ? 0 : -1}
                    title={cell.title}
                    aria-label={`${courtText.get(court.key) ?? court.key} ${friendlyTime12h(time)}: ${cell.title}`}
                    data-slot={time}
                    data-court={court.key}
                    data-row={rowIndex}
                    data-col={courtIdx}
                    onFocus={() => {
                      if (!focusPos || focusPos.row !== rowIndex || focusPos.col !== courtIdx) {
                        setFocusPos({ row: rowIndex, col: courtIdx });
                      }
                    }}
                    onKeyDown={(event) => handleCellKeyDown(event, rowIndex, courtIdx)}
                    onClick={(event) => {
                      // El segundo clic de un doble clic (p. ej. sobre la
                      // tarjeta recién elegida, que deja pasar los toques) no
                      // cuenta: elegir un partido nunca propone una hora.
                      if (event.detail > 1) return;
                      if (cell.enabled) onCellClick(court.key, time, { viaKeyboard: event.detail === 0 });
                      else onDisabledCellClick?.(court.key, time, cell);
                    }}
                  >
                    {cell.enabled ? <span className="schedule-timegrid__cell-hint">{time}</span> : null}
                  </button>
                );
              })}
              {previewPlacement && previewPlacement.row === rowIndex ? (
                <div
                  className="schedule-timegrid__preview"
                  style={itemStyle(
                    previewPlacement.row,
                    previewPlacement.span,
                    previewPlacement.column,
                    previewPlacement.offsetTop,
                    previewPlacement.offsetBottom,
                    headRows,
                  )}
                  aria-hidden="true"
                  data-preview-court={preview?.court}
                  data-preview-start={preview ? timeFromMinutes(preview.startMin) : undefined}
                >
                  <span>
                    {preview ? `${timeFromMinutes(preview.startMin)}–${timeFromMinutes(Math.min(preview.endMin, 24 * 60))}` : ""}
                  </span>
                </div>
              ) : null}
              {rowItems.map(({ item, row, span, offsetTop, offsetBottom, layout }) => (
                <div
                  key={item.key}
                  className={`schedule-timegrid__item${passThroughKey === item.key ? " is-passthrough" : ""}${
                    layout.half ? " is-half" : ""
                  }`}
                  style={{
                    ...itemStyle(row, span, layout.column, offsetTop, offsetBottom, headRows, layout.columnSpan),
                    ["--lane" as string]: String(layout.lane),
                    ["--lanes" as string]: String(layout.lanes),
                  }}
                  data-item-key={item.key}
                  data-item-half={layout.half ?? undefined}
                >
                  {renderItem(item, {
                    lane: layout.lane,
                    lanes: layout.lanes,
                    conflict: layout.conflict,
                    span,
                    half: layout.half,
                    narrow: layout.narrow,
                  })}
                </div>
              ))}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

export interface CourtTimeGridProposal {
  court: string;
  courtLabel: string;
  startMin: number;
  endMin: number;
}

interface CourtTimeGridBarProps {
  /** Texto cuando no hay partido elegido (o en modo Bloquear/Reservar). */
  hint: ReactNode;
  /** Partido elegido para programar/mover; null = ninguno. */
  selection: { verb: "Mover" | "Programar"; label: string; durationMinutes: number } | null;
  proposal: CourtTimeGridProposal | null;
  canEarlier: boolean;
  canLater: boolean;
  onNudge: (deltaMinutes: number) => void;
  onConfirm: () => void;
  onCancelProposal: () => void;
  onClearSelection: () => void;
  /** Explicación del último toque en una celda que no admite inicio. */
  notice: string | null;
  busy?: boolean;
  /** Fecha del grid (YYYY-MM-DD): se repite en palabras arriba de las
   * canchas para no programar en otro día por error (Eduardo 2026-10-01). */
  date?: string | null;
  /** Hoy (YYYY-MM-DD): con él la fecha dice "Hoy" o "No es hoy". */
  today?: string;
  /** Qué pasa al Confirmar (Publicar horarios, Eduardo 2026-10-01): «Sin
   * publicar: nadie recibe aviso.» o «Ya publicado: se avisará a sus
   * jugadores…». Se ve debajo del «¿Confirmas?». */
  consequence?: ReactNode;
  /** Lo que el servidor pidió revisar tras tocar Confirmar (orden de rondas,
   * Eduardo 2026-10-03): «Semifinal 1 empezaría antes de que terminen…».
   * Se queda en la barra hasta cambiar la propuesta. */
  warning?: ReactNode;
  /** Texto del botón de confirmar («Confirmar de todos modos» con aviso). */
  confirmLabel?: string;
}

/** Barra fija sobre el grid (siempre presente: elegir un partido ya no
 * empuja el grid hacia abajo). Tocar una hora solo PROPONE el cambio; nada se
 * guarda ni se avisa a los jugadores hasta Confirmar (revisión #418: con
 * filas de 15 min un doble clic o un dedo un poco corrido movía el partido). */
export const CourtTimeGridBar = forwardRef<HTMLButtonElement, CourtTimeGridBarProps>(function CourtTimeGridBar(
  {
    hint,
    selection,
    proposal,
    canEarlier,
    canLater,
    onNudge,
    onConfirm,
    onCancelProposal,
    onClearSelection,
    notice,
    busy = false,
    date = null,
    today,
    consequence = null,
    warning = null,
    confirmLabel = "Confirmar",
  },
  confirmRef,
) {
  const barRef = useRef<HTMLDivElement | null>(null);
  const proposalKey = proposal ? `${proposal.court}|${proposal.startMin}` : "";
  useEffect(() => {
    // Con el grid desplazado (o en el celular) la barra puede quedar arriba,
    // fuera de la vista o bajo los encabezados fijos: se trae a la vista para
    // que Confirmar se vea junto a la hora propuesta.
    if (!proposalKey) return;
    const bar = barRef.current;
    if (!bar || typeof bar.scrollIntoView !== "function") return;
    const rect = bar.getBoundingClientRect();
    if (rect.top < 112 || rect.bottom > window.innerHeight) {
      bar.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [proposalKey]);
  const longDate = formatLongDateEs(date);
  const isToday = Boolean(date && today && date === today);
  const proposalText = proposal
    ? `${proposal.courtLabel} · ${friendlyTime12h(timeFromMinutes(proposal.startMin))} a ${friendlyTime12h(
        timeFromMinutes(Math.min(proposal.endMin, 23 * 60 + 59)),
      )}`
    : "";
  return (
    <div
      ref={barRef}
      className={`schedule-timegrid__bar${proposal ? " has-proposal" : ""}${selection ? " has-selection" : ""}`}
      data-no-toast
    >
      {longDate ? (
        <p className={`schedule-timegrid__bar-date${today && !isToday ? " is-other-day" : ""}`} data-calendar-date-heading>
          <Icon name="calendar" className="schedule-timegrid__bar-date-icon" />
          <span className="schedule-timegrid__bar-date-text">{longDate}</span>
          {today ? (
            <span className="schedule-timegrid__bar-date-tag">{isToday ? "Hoy" : "No es hoy"}</span>
          ) : null}
        </p>
      ) : null}
      <div className="schedule-timegrid__bar-main" role="status" aria-live="polite">
        {selection && proposal ? (
          <span>
            {selection.verb} <strong>{selection.label}</strong> a <strong>{proposalText}</strong> ¿Confirmas?
          </span>
        ) : selection ? (
          <span>
            {selection.verb === "Mover" ? "Moviendo" : "Programando"}: <strong>{selection.label}</strong> · toca la
            hora de inicio ({selection.durationMinutes} min).
          </span>
        ) : (
          <span>{hint}</span>
        )}
      </div>
      {selection && proposal ? (
        <div className="schedule-timegrid__bar-actions">
          <button
            type="button"
            className="btn btn--ghost btn--compact"
            onClick={() => onNudge(-15)}
            disabled={busy || !canEarlier}
            aria-label="Empezar 15 minutos antes"
            title={canEarlier ? "Empezar 15 minutos antes" : "15 minutos antes no cabe"}
          >
            −15 min
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--compact"
            onClick={() => onNudge(15)}
            disabled={busy || !canLater}
            aria-label="Empezar 15 minutos después"
            title={canLater ? "Empezar 15 minutos después" : "15 minutos después no cabe"}
          >
            +15 min
          </button>
          <button
            type="button"
            ref={confirmRef}
            className="btn btn--primary btn--compact"
            onClick={onConfirm}
            disabled={busy}
          >
            {confirmLabel}
          </button>
          <button type="button" className="btn btn--ghost btn--compact" onClick={onCancelProposal} disabled={busy}>
            Cancelar
          </button>
        </div>
      ) : selection ? (
        <div className="schedule-timegrid__bar-actions">
          <button type="button" className="btn btn--ghost btn--compact" onClick={onClearSelection}>
            Limpiar selección
          </button>
        </div>
      ) : null}
      {selection && proposal && consequence ? (
        <p className="schedule-timegrid__bar-consequence" data-bar-consequence>
          {consequence}
        </p>
      ) : null}
      {selection && proposal && warning ? (
        <p className="schedule-timegrid__bar-warning" data-bar-warning>
          {warning}
        </p>
      ) : null}
      {notice ? <p className="schedule-timegrid__bar-notice">{notice}</p> : null}
    </div>
  );
});
